/**
 * Client: fetch Mux engagement gate for DualCTA mid-roll experiment.
 */

export type EngagementGateSource = "mux_hotspot" | "fallback_pct" | "none";

export type EngagementGatePayload = {
  playback_id: string;
  timeframe: string;
  total_views: number;
  /** From Mux Video API when server resolved it (or client-supplied). */
  duration_seconds?: number | null;
  gate: {
    source: EngagementGateSource;
    start_seconds: number | null;
    end_seconds: number | null;
    score: number | null;
    reason: string;
  };
  hotspots: { start_ms: number; end_ms: number; score: number }[];
  fetched_at: string;
  error?: string;
};

const clientCache = new Map<string, { at: number; body: EngagementGatePayload }>();
const CLIENT_TTL_MS = 5 * 60 * 1000;

/**
 * Mirror server minGateForDuration — short teasers (~14–17s DualCTA) must not
 * force 8s min or the hotspot (often ~6–7s) is pushed late / past peak.
 */
export function minGateForDuration(duration: number | null | undefined): number {
  if (duration == null || !(duration > 0)) return 8;
  if (duration < 20) return Math.max(4, duration * 0.35);
  if (duration < 40) return Math.max(6, duration * 0.25);
  return 8;
}

/** Clamp gate seconds once duration is known from the player / server. */
export function clampGateSeconds(
  startSeconds: number | null | undefined,
  durationSeconds: number | null | undefined,
): number | null {
  if (startSeconds == null || !Number.isFinite(startSeconds)) return null;
  const minGate = minGateForDuration(durationSeconds);
  let s = Math.max(minGate, startSeconds);
  if (durationSeconds != null && durationSeconds > 10) {
    const maxGate = durationSeconds * 0.85;
    s = Math.min(s, maxGate);
    // Very short videos: no mid-roll (would fire immediately)
    if (durationSeconds < 12) return null;
  }
  return Math.round(s * 10) / 10;
}

export async function fetchMuxEngagementGate(
  playbackId: string,
  opts?: {
    timeframe?: string;
    durationSeconds?: number | null;
    signal?: AbortSignal;
    /** Force network when player duration just became known. */
    bypassCache?: boolean;
  },
): Promise<EngagementGatePayload | null> {
  const pid = String(playbackId || "").trim();
  if (pid.length < 10) return null;

  const timeframe = opts?.timeframe || "7:days";
  const cacheKey = `${pid}|${timeframe}`;
  const hit = clientCache.get(cacheKey);
  const callerDur =
    opts?.durationSeconds != null && Number.isFinite(opts.durationSeconds)
      ? opts.durationSeconds
      : null;

  if (!opts?.bypassCache && hit && Date.now() - hit.at < CLIENT_TTL_MS) {
    const cachedDur = hit.body.duration_seconds;
    // Re-fetch when player/server duration is new but cache was built without it
    // (old "assume 20s" / fixed-18s gates).
    const needDurationRefetch =
      callerDur != null &&
      callerDur > 0 &&
      !(typeof cachedDur === "number" && cachedDur > 0);

    if (!needDurationRefetch) {
      const body = hit.body;
      if (callerDur != null && body.gate?.start_seconds != null) {
        const refined = clampGateSeconds(body.gate.start_seconds, callerDur);
        if (refined != null && refined !== body.gate.start_seconds) {
          return {
            ...body,
            duration_seconds: callerDur,
            gate: {
              ...body.gate,
              start_seconds: refined,
              end_seconds:
                body.gate.end_seconds != null
                  ? Math.min(
                      body.gate.end_seconds,
                      refined + 3,
                    )
                  : refined + 2,
              reason: `${body.gate.reason}; client_clamp duration=${callerDur}s`,
            },
          };
        }
      }
      return body;
    }
  }

  const q = new URLSearchParams({
    playback_id: pid,
    timeframe,
  });
  if (callerDur != null) {
    q.set("duration_seconds", String(Math.round(callerDur)));
  }

  try {
    const res = await fetch(`/api/mux-engagement?${q.toString()}`, {
      method: "GET",
      signal: opts?.signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as EngagementGatePayload;
    if (!body?.gate) return null;

    // Prefer server duration, then caller
    const resolvedDur =
      (typeof body.duration_seconds === "number" && body.duration_seconds > 0
        ? body.duration_seconds
        : null) ?? callerDur;
    if (resolvedDur != null && body.gate.start_seconds != null) {
      const refined = clampGateSeconds(body.gate.start_seconds, resolvedDur);
      if (refined != null) {
        body.gate = {
          ...body.gate,
          start_seconds: refined,
        };
        body.duration_seconds = resolvedDur;
      }
    }

    clientCache.set(cacheKey, { at: Date.now(), body });
    return body;
  } catch {
    return null;
  }
}

/** Telemetry props shared on DualCTA events when gate experiment is active. */
export function engagementGateTelemetryProps(input: {
  arm: string;
  gateSource?: string | null;
  gateSeconds?: number | null;
  hotspotScore?: number | null;
  muxTotalViews?: number | null;
  assignmentSource?: string | null;
  rolloutPct?: number | null;
}): Record<string, string | number | boolean | null> {
  return {
    engagement_gate_arm: input.arm,
    gate_source: input.gateSource ?? null,
    gate_seconds: input.gateSeconds ?? null,
    hotspot_score: input.hotspotScore ?? null,
    mux_total_views: input.muxTotalViews ?? null,
    engagement_gate_assignment_source: input.assignmentSource ?? null,
    engagement_gate_rollout_pct: input.rolloutPct ?? null,
  };
}
